import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import Modal from '@deliveroo/consumer-component-library/components/Modal';
import classnames from 'classnames';
import { buildShortSearchResults, getSearchPlaceholder } from '../../selectors/homeSelectors';
import { getTargetURL } from '../../utils/home/factories';
import trackBeforeRedirect from '../../utils/home/trackBeforeRedirect';

import {
  requestSearchResults,
  requestHomeFeed,
  clearSearchResults,
  setHomeQuery,
} from '../../actions/homeFeedActions';
import {
  trackTappedSearchBar,
  trackTypedInSearchBar,
  trackTappedSearchResult,
  trackViewedSearchDropDown,
} from '../../actions/tracking/homeSearch';
import HomeSearch from '../../components/HomeSearch';
import HomeSuggestionBox from '../../components/HomeSuggestionBox';
import { SUPPORTED_TARGETS } from '../../utils/home/constants';
import styles from './styles.scss';

export class HomeSearchContainer extends Component {
  state = {
    value: '',
    showSuggestions: false,
    modalIsOpen: false,
    selectedResult: -1,
    keyboardSelectActive: false,
  };

  wrapper = React.createRef();

  componentDidMount() {
    document.addEventListener('keydown', this.handleWindowKeyDown, false);
    document.addEventListener('mousedown', this.handleOnClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleWindowKeyDown, false);
    document.removeEventListener('mousedown', this.handleOnClickOutside);
  }

  setShowSuggestionsState = (showSuggestions) => {
    this.setState({ showSuggestions });
  };

  setModalState = (modalIsOpen) => {
    this.setState({ modalIsOpen });
  };

  searchHasValueAndResults = () => Boolean(this.props.searchResults.length && this.state.value);

  suggestionsVisible = () => this.searchHasValueAndResults() && this.state.showSuggestions;

  handleOnClickOutside = (event) => {
    if (!this.props.isMobile && this.wrapper && !this.wrapper.current.contains(event.target)) {
      this.handleInputFocus(false);
      this.setShowSuggestionsState(false);
    }
  };

  handleOnChange = (event) => {
    const searchTerm = event.target.value;
    this.setShowSuggestionsState(true);
    this.setState({
      value: searchTerm,
    });
    this.props.requestSearchResults({ query: searchTerm });

    if (event.target.value === '') {
      this.props.clearSearchResults();
    }
  };

  handleSelectResult = ({ event, target }) => {
    const track = () => this.props.trackTappedSearchResult(this.state.value, target);
    if ([SUPPORTED_TARGETS.PARAMS].includes(target.typeName)) {
      this.setState({ value: target.title, selectedResult: -1 });
      this.props.requestHomeFeed({ selectedFilters: [target.queryParams], clearCollection: true });
      this.setModalState(false);
      track();
    } else if (
      [SUPPORTED_TARGETS.RESTAURANT, SUPPORTED_TARGETS.MENU_ITEM].includes(target.typeName)
    ) {
      trackBeforeRedirect(event, getTargetURL(target), track);
    }

    this.setShowSuggestionsState(false);
  };

  registerSearchInput = (input) => {
    this.searchInput = input;
  };

  handleOnClickViewAll = () => {
    this.props.requestHomeFeed({
      selectedFilters: [`query=${this.state.value}`],
      clearCollection: true,
    });
    this.setShowSuggestionsState(false);
    this.setState({
      modalIsOpen: false,
    });
  };

  handleOnSubmitMobile = () => {
    this.props.requestHomeFeed({
      selectedFilters: [`query=${this.state.value}`],
      clearCollection: true,
    });
    this.setShowSuggestionsState(false);
    this.setModalState(false);
  };

  handleOnClear = () => {
    this.setShowSuggestionsState(false);
    this.setState({ value: '' });
    this.props.clearSearchResults();
  };

  handleClickTextInput = () => {
    if (this.props.isMobile) {
      this.toggleModal();
    }

    // When there are results in state and the user re-focusses the input
    if (this.searchHasValueAndResults()) {
      this.setShowSuggestionsState(true);
    }

    // When a new feed has been requested so searchResults are empty,
    // but the searched term is populated in the search box and the
    // user re-focusses
    if (this.state.value && !this.props.searchResults.length) {
      this.setShowSuggestionsState(true);
      this.props.requestSearchResults({ query: this.state.value });
    }

    this.props.trackTappedSearchBar();
  };

  toggleModal = () => {
    this.setState((state) => ({
      ...state,
      modalIsOpen: !state.modalIsOpen,
    }));
  };

  handleWindowKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.setShowSuggestionsState(false);
      this.setModalState(false);
      this.setState({
        selectedResult: -1,
      });
      this.handleInputFocus(false);
    } else if (event.key === '/') {
      event.preventDefault();
      this.handleInputFocus(true);
      this.setShowSuggestionsState(true);
    }
  };

  handleInputFocus = (focus) => {
    const searchInput = this.searchInput && this.searchInput.current;
    if (searchInput && focus) {
      searchInput.focus();
    } else if (searchInput && !focus) {
      searchInput.blur();
    }
  };

  handleInputKeyDown = (event) => {
    if (!this.suggestionsVisible()) return;

    const { searchResults } = this.props;
    const { selectedResult } = this.state;
    const totalResults = searchResults
      .map((r) => (r.options ? r.options.length : 0))
      .reduce((a, b) => a + b, 0);

    if (event.key === 'ArrowUp' && selectedResult > -1) {
      this.handlePressArrowUp(event);
    } else if (event.key === 'ArrowDown' && selectedResult < totalResults - 1) {
      this.handlePressArrowDown(event);
    }
  };

  handleOnSubmitDesktop = (event) => {
    if (!this.suggestionsVisible()) return;

    if (this.state.selectedResult < 0) {
      this.setModalState(false);
      event.preventDefault();
      this.props.requestHomeFeed({
        selectedFilters: [`query=${this.state.value}`],
        clearCollection: true,
      });
    } else {
      this.handleSelectResult({ target: this.getSelectedResultTarget(), event });
    }
    this.setShowSuggestionsState(false);
    this.handleInputFocus(false);
  };

  handlePressArrowUp = (event) => {
    event.preventDefault();
    this.setState((state) => ({
      selectedResult: state.selectedResult - 1,
      keyboardSelectActive: true,
    }));
  };

  handlePressArrowDown = (event) => {
    event.preventDefault();
    this.setState((state) => ({
      selectedResult: state.selectedResult + 1,
      keyboardSelectActive: true,
    }));
  };

  getSelectedResultTarget = () => {
    const { searchResults } = this.props;
    const { selectedResult } = this.state;
    const flattenedResults = searchResults.reduce((arr, result) => arr.concat(result.options), []);
    return flattenedResults[selectedResult].target;
  };

  handleOnTouchMove = () => {
    this.handleInputFocus(false);
  };

  handleMouseOver = () => {
    if (this.state.keyboardSelectActive) {
      this.setState({
        keyboardSelectActive: false,
        selectedResult: -1,
      });
    }
  };

  render() {
    const { value, selectedResult, keyboardSelectActive } = this.state;
    const { searchResults, isMobile, isLoading, searchPlaceholder } = this.props;

    const homeSearchSharedProps = {
      handleOnClear: this.handleOnClear,
      onChange: this.handleOnChange,
      handleOnKeyDown: this.handleInputKeyDown,
      value,
      searchPlaceholder,
    };

    const homeSuggestionBoxSharedProps = {
      isLoading,
      data: searchResults,
      onClickResult: this.handleSelectResult,
      onClickViewAll: this.handleOnClickViewAll,
      trackViewedSearchDropDown: this.props.trackViewedSearchDropDown,
    };

    const showSuggestions =
      (this.searchHasValueAndResults() && this.state.showSuggestions) || isLoading;
    const showMobileSuggestions = isMobile && showSuggestions;
    const showDesktopSuggestions = !isMobile && showSuggestions;

    const wrapperClasses = classnames(styles.wrapper, {
      [styles.isMobile]: isMobile,
    });

    return (
      <div className={wrapperClasses} ref={this.wrapper}>
        <HomeSearch
          {...homeSearchSharedProps}
          handleOnClick={this.handleClickTextInput}
          returnInputRef={this.registerSearchInput}
          handleOnSubmit={this.handleOnSubmitDesktop}
          isMobile={isMobile}
        >
          {showDesktopSuggestions && (
            <HomeSuggestionBox
              {...homeSuggestionBoxSharedProps}
              selectedResult={selectedResult}
              handleOnMouseOver={this.handleMouseOver}
              keyboardSelectActive={keyboardSelectActive}
            />
          )}
        </HomeSearch>
        <Modal
          id="search-modal"
          appElement="#__next"
          type="fullscreen"
          isOpen={this.state.modalIsOpen}
        >
          <div className={styles.modalContentWrapper}>
            <HomeSearch
              {...homeSearchSharedProps}
              isFocused
              isModalSearch
              handleOnClose={this.toggleModal}
              handleOnSubmit={this.handleOnSubmitMobile}
              returnInputRef={this.registerSearchInput}
            />
            {showMobileSuggestions && (
              <HomeSuggestionBox
                {...homeSuggestionBoxSharedProps}
                isModalResults
                handleOnTouchMove={this.handleOnTouchMove}
              />
            )}
          </div>
        </Modal>
      </div>
    );
  }
}

HomeSearchContainer.propTypes = {
  isMobile: PropTypes.bool,
  requestSearchResults: PropTypes.func.isRequired,
  requestHomeFeed: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  searchResults: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      key: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string,
          count: PropTypes.number,
          highlights: PropTypes.arrayOf(
            PropTypes.shape({
              begin: PropTypes.number,
              end: PropTypes.number,
            }),
          ),
          image: PropTypes.shape({
            type: PropTypes.string,
            default: PropTypes.string,
          }),
          label: PropTypes.string,
          target: PropTypes.shape({
            typeName: PropTypes.string,
            restaurant: PropTypes.shape({
              name: PropTypes.string,
              links: PropTypes.shape({
                self: PropTypes.shape({
                  href: PropTypes.string,
                }),
              }),
            }),
            params: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string,
                value: PropTypes.arrayOf(PropTypes.string),
              }),
            ),
            queryParams: PropTypes.string,
            title: PropTypes.string,
          }),
        }),
      ),
    }),
  ).isRequired,
  clearSearchResults: PropTypes.func.isRequired,
  trackTappedSearchBar: PropTypes.func.isRequired,
  trackTappedSearchResult: PropTypes.func.isRequired,
  trackViewedSearchDropDown: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

HomeSearchContainer.defaultProps = {
  isMobile: false,
};

export const mapStateToProps = (state) => ({
  searchResults: buildShortSearchResults(state),
  searchPlaceholder: getSearchPlaceholder(state),
  isLoading: state.home.feed.loadingSearch,
});

const mapDispatchToProps = (dispatch) => ({
  requestSearchResults: debounce((queryObject) => {
    if (queryObject.query !== '') {
      dispatch(trackTypedInSearchBar(queryObject));
      dispatch(requestSearchResults(queryObject));
    }
    dispatch(setHomeQuery(queryObject));
  }, 350),
  clearSearchResults: () => dispatch(clearSearchResults()),
  requestHomeFeed: (params) => dispatch(requestHomeFeed(params)),
  trackTappedSearchBar: () => dispatch(trackTappedSearchBar()),
  trackTappedSearchResult: (searchTerm, target) =>
    dispatch(trackTappedSearchResult(searchTerm, target)),
  trackViewedSearchDropDown: (results) => dispatch(trackViewedSearchDropDown(results)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HomeSearchContainer);
